import { _ } from "../core/polyglot/polyglot.js";
import CompanyAdminRole from "./role/CompanyAdminRole.js";
import CompanyNonAdminRole from "./role/CompanyNonAdminRole.js";
import Model from "./Model.js";

const companyAdminRoleName = "CompanyAdminRole";
const companyImportRoleName = "CompanyImportRole";
const companyNormalRoleName = "CompanyNormalRole";
const companyLimitedRoleName = "CompanyLimitedRole";
const companyDocumentArchiveRoleName = "CompanyDocumentArchiveRole";

/**
 * A user's role within a company.
 *
 * @param {string} spec.companyId - Id of the company to which the role refers
 * @param {string} spec.typeOfRole - Type of role, to pick among [CompanyAdminRole, CompanyImportRole, CompanyNormalRole, CompanyLimitedRole]
 * @return {companyRole}
 */
export default class CompanyRole extends Model {
	constructor({ companyId, typeOfRole } = {}) {
		super(...arguments);

		this._companyId = companyId;
		this._typeOfRole = typeOfRole;
	}

	getCompanyId() {
		return this._companyId;
	}

	setCompanyId(companyId) {
		this._companyId = companyId;
	}

	getTypeOfRole() {
		return this._typeOfRole;
	}

	setTypeOfRole(typeOfRole) {
		if (!this.constructor.allRoles().includes(typeOfRole)) {
			throw `Unknown company role: ${typeOfRole}`;
		}

		this._typeOfRole = typeOfRole;
	}

	isAdminRole() {
		return this._typeOfRole === companyAdminRoleName;
	}

	isImportRole() {
		return this._typeOfRole === companyNormalRoleName;
	}

	isNormalRole() {
		return this._typeOfRole === companyNormalRoleName;
	}

	isLimitedRole() {
		return this._typeOfRole === companyLimitedRoleName;
	}

	isDocumentArchiveRole() {
		return this._typeOfRole === companyDocumentArchiveRoleName;
	}

	toString() {
		return `${this._companyId}: ${this._typeOfRole}`;
	}

	getPresentationName() {
		return this.constructor.getPresentationName(this._typeOfRole);
	}

	hasRole(roles) {
		if (!roles || !roles.length) {
			return false;
		}

		if (roles.includes("limited")) {
			return [
				companyAdminRoleName,
				companyImportRoleName,
				companyNormalRoleName,
				companyLimitedRoleName,
			].includes(this._typeOfRole);
		}

		if (roles.includes("normal")) {
			return [
				companyAdminRoleName,
				companyImportRoleName,
				companyNormalRoleName,
			].includes(this._typeOfRole);
		}

		if (roles.includes("import")) {
			return [companyAdminRoleName, companyImportRoleName].includes(
				this._typeOfRole,
			);
		}

		if (roles.includes("admin")) {
			return this._typeOfRole === companyAdminRoleName;
		}

		return true;
	}

	// This method is there to support the generation of proper
	// roles (instances of a subclass of `role`) while still
	// keeping companyRole (the current file) in the model. This
	// whole file will get removed in the context of FTGP-5715.
	toNewStyleRole(agencyId) {
		const spec = {
			companyIds: [this._companyId],
			agencyId,
		};

		return this._typeOfRole === companyAdminRoleName
			? new CompanyAdminRole(spec)
			: // to be extended when finer-grained roles are
				// introduced
				new CompanyNonAdminRole(spec);
	}

	_serializedProperties() {
		const data = super._serializedProperties();

		data.companyId = this._companyId;
		data.typeOfRole = this._typeOfRole;

		return data;
	}

	static getPresentationName(role) {
		switch (role) {
			case companyAdminRoleName: {
				return _("Administrator");
			}
			case companyImportRoleName: {
				return _("Normal user with import");
			}
			case companyNormalRoleName: {
				return _("Normal user");
			}
			case companyLimitedRoleName: {
				return _("Limited user");
			}
			case companyDocumentArchiveRoleName: {
				return _("Document archive user");
			}
			default: {
				return _("n/a");
			}
		}
	}

	static getCompanyAdminRoleName() {
		return companyAdminRoleName;
	}

	static getCompanyImportRoleName() {
		return companyImportRoleName;
	}

	static getCompanyNormalRoleName() {
		return companyNormalRoleName;
	}

	static getCompanyLimitedRoleName() {
		return companyLimitedRoleName;
	}

	static getCompanyDocumentArchiveRoleName() {
		return companyDocumentArchiveRoleName;
	}

	static allRoles() {
		return [
			companyAdminRoleName,
			companyImportRoleName,
			companyNormalRoleName,
			companyLimitedRoleName,
			companyDocumentArchiveRoleName,
		];
	}
}
